/**
 * Bank-detail fields used by AccountForm. Extracted to a sibling file to keep
 * AccountForm.tsx under the max-lines threshold. Not part of the public API.
 */

import { Input, FormField, Skeleton } from '@elsapiens/ui'

import { getBankCodeLabel, type AccountFormData } from './chart-of-accounts-helpers'

interface BankDetailsSectionProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isLoadingBankDetails: boolean
}

export function BankDetailsSection({ formData, setFormData, isLoadingBankDetails }: BankDetailsSectionProps): JSX.Element {
  return (
    <>
      <div className="border-t border-border el-pt-sm">
        <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Bank Details</h4>
      </div>

      {isLoadingBankDetails ? (
        <BankDetailsSkeleton currency={formData.currency} />
      ) : (
        <BankDetailsFields formData={formData} setFormData={setFormData} />
      )}
    </>
  )
}

function BankDetailsSkeleton({ currency }: { currency: string }): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <div className="grid grid-cols-2 el-gap-field">
        <FormField label="Bank Name"><Skeleton className="h-9 w-full rounded" /></FormField>
        <FormField label="Branch"><Skeleton className="h-9 w-full rounded" /></FormField>
      </div>
      <FormField label="Account Number"><Skeleton className="h-9 w-full rounded" /></FormField>
      <FormField label={getBankCodeLabel(currency)}>
        <Skeleton className="h-9 w-full rounded" />
      </FormField>
    </div>
  )
}

function BankDetailsFields({ formData, setFormData }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>> }): JSX.Element {
  return (
    <>
      <FormField helperText="Account holder name as registered with the bank (used on invoices)" label="Name at Bank">
        <Input
          placeholder="e.g., Acme Pvt Ltd"
          value={formData.nameAtBank}
          onChange={e => setFormData(prev => ({ ...prev, nameAtBank: e.target.value }))}
        />
      </FormField>

      <div className="grid grid-cols-2 el-gap-field">
        <FormField label="Bank Name">
          <Input
            placeholder="e.g., HDFC Bank, Chase, Wise"
            value={formData.bankName}
            onChange={e => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
          />
        </FormField>
        <FormField label="Branch">
          <Input
            placeholder="e.g., Mumbai Main, New York"
            value={formData.branch}
            onChange={e => setFormData(prev => ({ ...prev, branch: e.target.value }))}
          />
        </FormField>
      </div>

      <FormField label="Account Number">
        <Input
          placeholder="Full account number"
          value={formData.accountNumber}
          onChange={e => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
        />
      </FormField>

      <BankIdentifiers formData={formData} setFormData={setFormData} />
    </>
  )
}

interface BankIdField {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
}

function IfscField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField required helperText="Required for domestic transfers" label="IFSC Code">
      <Input
        maxLength={11}
        placeholder="e.g., HDFC0001234"
        value={formData.ifscCode}
        onChange={e => setFormData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
      />
    </FormField>
  )
}

function SwiftField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField helperText="For international transfers" label="SWIFT/BIC Code">
      <Input
        maxLength={11}
        placeholder="e.g., CHASUS33XXX"
        value={formData.swiftCode}
        onChange={e => setFormData(prev => ({ ...prev, swiftCode: e.target.value.toUpperCase() }))}
      />
    </FormField>
  )
}

function RoutingField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField helperText="9-digit routing number" label="ABA Routing Number">
      <Input
        maxLength={9}
        placeholder="e.g., 021000021"
        value={formData.routingNumber}
        onChange={e => setFormData(prev => ({ ...prev, routingNumber: e.target.value }))}
      />
    </FormField>
  )
}

function SortCodeField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField helperText="6-digit sort code (XX-XX-XX)" label="Sort Code">
      <Input
        maxLength={8}
        placeholder="e.g., 12-34-56"
        value={formData.sortCode}
        onChange={e => setFormData(prev => ({ ...prev, sortCode: e.target.value }))}
      />
    </FormField>
  )
}

function IbanField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <div className="col-span-2">
      <FormField helperText="International Bank Account Number (up to 34 characters)" label="IBAN">
        <Input
          maxLength={34}
          placeholder="e.g., GB29NWBK60161331926819"
          value={formData.iban}
          onChange={e => setFormData(prev => ({ ...prev, iban: e.target.value.toUpperCase().replace(/\s/g, '') }))}
        />
      </FormField>
    </div>
  )
}

function BankIdentifiers({ formData, setFormData }: BankIdField): JSX.Element {
  const props: BankIdField = { formData, setFormData }
  return (
    <>
      <div className="border-t border-border el-pt-sm">
        <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">
          Bank Identifiers {formData.currency && <span className="text-foreground-secondary font-normal">({formData.currency})</span>}
        </h4>
      </div>

      <div className="grid grid-cols-2 el-gap-field">
        {formData.currency === 'INR' && <IfscField {...props} />}
        <SwiftField {...props} />
        {formData.currency === 'USD' && <RoutingField {...props} />}
        {formData.currency === 'GBP' && <SortCodeField {...props} />}
        {['EUR', 'GBP', 'AED'].includes(formData.currency) && <IbanField {...props} />}
      </div>
    </>
  )
}
